// =====================================================================
// src/lib/usuarios.js — o que a tela de Usuários e perfis decide, sem DOM
//
// Funções PURAS sobre o que o banco devolve (usuarios_do_cliente, papeis com
// permissoes, modulos, invariantes_proibicao, solicitacoes_permissao). A
// regra de quem pode o quê está no banco (0013); aqui só se arruma, filtra,
// rotula e prepara o pedido — e por isso tudo é testável em node.
// =====================================================================

// ── a lista de pessoas ───────────────────────────────────────────────
// Situação de uma pessoa, na ordem em que importa para quem administra:
// desligada > convite pendente > nunca entrou (login antigo) > ativa.
export function situacao(u) {
  if (!u.ativo) return { chave: 'inativo', rotulo: 'Desativado', classe: 'badge-gray' };
  if (u.convite_pendente) return { chave: 'convite', rotulo: 'Convite pendente', classe: 'badge-yellow' };
  if (!u.ultimo_acesso) return { chave: 'nunca', rotulo: 'Nunca entrou', classe: 'badge-gray' };
  return { chave: 'ativo', rotulo: 'Ativo', classe: 'badge-green' };
}

// "há 3 dias", "hoje", "há 2 meses" — para o último acesso. Nunca "há -1 dia".
export function haQuanto(iso, agora = Date.now()) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const dias = Math.floor(Math.max(0, agora - t) / 86400000);
  if (dias === 0) return 'hoje';
  if (dias === 1) return 'ontem';
  if (dias < 30) return `há ${dias} dias`;
  const meses = Math.floor(dias / 30);
  if (meses < 12) return meses === 1 ? 'há 1 mês' : `há ${meses} meses`;
  const anos = Math.floor(dias / 365);
  return anos === 1 ? 'há 1 ano' : `há ${anos} anos`;
}

const semAcento = s => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

// Filtro da lista: texto (nome, e-mail, papel) e "mostrar desativados".
// A ordem vem do banco (ativos primeiro, por nome) e não se mexe nela.
export function filtrarPessoas(lista, { texto = '', comInativos = false } = {}) {
  const t = semAcento(texto).trim();
  return (lista || []).filter(u => {
    if (!comInativos && !u.ativo) return false;
    if (!t) return true;
    return [u.nome, u.email, u.papel_nome, u.imobiliaria_nome].some(c => semAcento(c).includes(t));
  });
}

// Agrupar por imobiliária: a equipe do prédio primeiro, depois cada
// imobiliária pelo nome. É como a Administração do Hotel pensa
// a casa; a Monteiro só vê o próprio grupo e a lista sai com um grupo só.
export function agruparPorImobiliaria(lista) {
  const grupos = new Map();
  for (const u of lista || []) {
    const chave = u.imobiliaria_id || '';
    if (!grupos.has(chave)) grupos.set(chave, { imobiliaria_id: u.imobiliaria_id || null, nome: u.imobiliaria_nome || 'Equipe do prédio', pessoas: [] });
    grupos.get(chave).pessoas.push(u);
  }
  return [...grupos.values()].sort((a, b) => (a.imobiliaria_id === null ? -1 : b.imobiliaria_id === null ? 1 : a.nome.localeCompare(b.nome)));
}

// Para o Administrador do Sistema a mesma lista vem de todos os clientes:
// agrupa por cliente. Quem não tem cliente_id na linha (não deveria haver)
// cai em "sem cliente" em vez de sumir.
export function agruparPorCliente(lista, clientes) {
  const nomes = new Map((clientes || []).map(c => [c.id, c]));
  const grupos = new Map();
  for (const u of lista || []) {
    const c = nomes.get(u.cliente_id);
    if (!grupos.has(u.cliente_id)) grupos.set(u.cliente_id, { cliente_id: u.cliente_id, nome: c ? c.nome : 'Sem cliente', slug: c ? c.slug : '', pessoas: [] });
    grupos.get(u.cliente_id).pessoas.push(u);
  }
  return [...grupos.values()].sort((a, b) => a.nome.localeCompare(b.nome));
}

// O que a pessoa logada pode fazer com cada linha: nunca consigo mesma
// (o banco recusa; a tela nem oferece), nunca com quem só vê.
export function acoesPara(u, { podeEditar, meuId }) {
  if (!podeEditar) return [];
  if (u.id === meuId) return ['renomear'];
  const a = ['editar'];
  a.push(u.ativo ? 'desativar' : 'reativar');
  if (u.ativo && u.convite_pendente) a.push('reenviar');
  return a;
}

// ── o convite ────────────────────────────────────────────────────────
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export function validarConvite({ nome, email, papel_id }) {
  const erros = [];
  if (!String(nome || '').trim()) erros.push('Nome é obrigatório');
  if (!EMAIL.test(String(email || '').trim())) erros.push('E-mail inválido');
  if (!papel_id) erros.push('Escolha um papel');
  return erros;
}

// Papéis que se oferecem no convite e na edição: ativos, do cliente, por
// nome. 0091 (decisão 2A do Hugo, 29/10/2026): login de imobiliária só
// recebe o papel de imobiliária — o `imobiliaria` de fábrica. O banco
// recusa o resto (`app.checar_papel_do_login`); a lista só não oferece o que
// seria recusado.
export const PAPEL_DE_IMOBILIARIA = 'imobiliaria';
export function papeisParaConvite(papeis, { imobiliaria = false } = {}) {
  // 0095: e o inverso — o papel da imobiliária não aparece para a equipe do hotel
  return (papeis || []).filter(p => p.ativo && (imobiliaria ? p.chave === PAPEL_DE_IMOBILIARIA : p.chave !== PAPEL_DE_IMOBILIARIA))
    .slice().sort((a, b) => a.nome.localeCompare(b.nome));
}

// ── perfis: a matriz papel × módulo ──────────────────────────────────
export const NIVEIS = Object.freeze(['nenhum', 'ver', 'editar']);

// Uma linha por papel, uma célula por módulo do catálogo: nível atual,
// e se é invariante (proibido: a célula não se edita e diz o motivo).
export function matrizDePermissoes(papeis, modulos, proibicoes) {
  const mods = (modulos || []).slice().sort((a, b) => (a.ordem - b.ordem) || String(a.chave).localeCompare(String(b.chave)));
  const proib = new Map((proibicoes || []).map(p => [p.papel_chave + '|' + p.modulo_chave, p.motivo]));
  return (papeis || []).slice().sort((a, b) => a.nome.localeCompare(b.nome)).map(p => {
    const niveis = new Map((p.permissoes || []).map(x => [x.modulo_chave, x.nivel]));
    return {
      papel_id: p.id, chave: p.chave, nome: p.nome, de_fabrica: !!p.de_fabrica, ativo: p.ativo !== false,
      celulas: mods.map(m => ({
        modulo: m.chave, modulo_nome: m.nome,
        nivel: niveis.get(m.chave) || 'nenhum',
        proibido: proib.get(p.chave + '|' + m.chave) || null,
      })),
    };
  });
}

// O pedido de mudança: só o que mudou de fato, no formato que
// solicitar_permissoes recebe ({modulo: nivel}). Célula proibida não entra
// nem que a tela tente. Vazio = nada a pedir (o banco também recusa).
export function pedidoDeMudanca(linha, novos) {
  const niveis = {};
  for (const c of linha.celulas) {
    const novo = novos[c.modulo];
    if (novo === undefined || novo === c.nivel) continue;
    if (!NIVEIS.includes(novo)) continue;
    if (c.proibido) continue;
    niveis[c.modulo] = novo;
  }
  return niveis;
}

// Texto de uma mudança pendente, como o banco a calculou (de → para).
export function descreverMudancas(mudancas, modulos) {
  const nomes = new Map((modulos || []).map(m => [m.chave, m.nome]));
  return (mudancas || []).map(m => `${nomes.get(m.modulo) || m.modulo}: ${m.de} → ${m.para}`);
}

// Pendências por papel, para a tela marcar "há um pedido esperando".
export function pendentesPorPapel(solicitacoes) {
  const por = new Map();
  for (const s of solicitacoes || []) if (s.status === 'pendente') por.set(s.papel_id, (por.get(s.papel_id) || 0) + 1);
  return por;
}

// ── 0071: o CPF de quem usa o sistema (vai no `cpf_solicitante` da FNRH) ──
// A mesma régua de `app.cpf_valido` (0071): 11 dígitos, os dois dígitos
// verificadores conferidos, e onze iguais recusados. A tela confere antes
// só para avisar na hora; quem decide é o banco.
export const cpfDigitos = s => String(s == null ? '' : s).replace(/\D/g, '');
export function cpfValido(s) {
  const d = cpfDigitos(s);
  if (!/^\d{11}$/.test(d) || /^(\d)\1{10}$/.test(d)) return false;
  const dv = n => { let t = 0; for (let i = 0; i < n; i++) t += Number(d[i]) * (n + 1 - i); const r = (t * 10) % 11; return r === 10 ? 0 : r; };
  return dv(9) === Number(d[9]) && dv(10) === Number(d[10]);
}
// Letra no campo é erro (o banco recusa igual); pontos, traço e espaço podem ir.
export function validarCpf(s) {
  const t = String(s == null ? '' : s).trim();
  if (!t) return null;                                   // vazio: a tela decide (manter)
  if (/[^\d.\-\s]/.test(t)) return 'CPF tem só números (pontos e traço podem ir)';
  if (!cpfValido(t)) return 'CPF inválido — confira os dígitos';
  return null;
}
